// Press Release API Service
import { createApiClient } from '../utils/axiosConfig';
import { getApiBaseUrl } from '../utils/apiConfig';

const API_BASE_URL = getApiBaseUrl();

// Create axios instance with default config and auth error handling
const apiClient = createApiClient(API_BASE_URL);

// Types
export interface PressReleaseResponse {
  _id: string;
  title: string;
  content: string;
  contactInfo: {
    name: string;
    email: string;
    phone?: string;
    company: string;
  };
  targetAudience: string[];
  distributionChannels: string[];
  author: {
    _id: string;
    fullName: string;
    email: string;
    company?: string;
  };
  status: 'draft' | 'submitted' | 'verified' | 'distributed' | 'published' | 'rejected';
  verificationStatus?: string;
  aiAnalysis?: any;
  analytics?: {
    totalImpressions?: number;
    totalClicks?: number;
    totalShares?: number;
    totalComments?: number;
    averageTimeOnPage?: number;
    sentimentScore?: number;
    publications?: Array<{
      outlet: string;
      url: string;
      publishedDate: string | Date;
      impressions: number;
      clicks: number;
      shares: number;
      tier: number;
    }>;
  };
  createdAt: string;
  updatedAt: string;
}

export interface OutletMetric {
  outlet: string;
  articleUrl: string;
  publishedDate: string;
  tier: number;
  clicks: number;
  shares: number;
  impressions: number;
  views?: number; // Views from ArticleMetrics (GA4 pageViews or estimatedViews)
  ctr: number | string;
}

export interface PRAnalyticsWithOutletsResponse {
  pressRelease: {
    id: string;
    title: string;
    status: string;
  };
  overallMetrics: {
    totalClicks: number;
    totalShares: number;
    totalImpressions: number;
    averageCTR: number | string;
    totalPublications: number;
  };
  outletMetrics: OutletMetric[];
  // historicalData and recommendations exist on the backend but are not
  // currently used by the frontend Publications page
}

export interface VerificationStatusApiResponse {
  verificationStatus?: string;
  documents?: any;
  aiAnalysis?: {
    structureScore?: number;
    readabilityScore?: number;
    seoScore?: number;
    contactCompleteness?: number;
    suggestions?: string[];
    confidenceScore?: number;
  };
}

export interface CreatePressReleaseRequest {
  title: string;
  content: string;
  contactInfo: {
    name: string;
    email: string;
    phone?: string;
    company: string;
  };
  targetAudience: string[];
  geographicRegions: any;
}

export interface UpdatePressReleaseRequest {
  title?: string;
  content?: string;
  contactInfo?: {
    name: string;
    email: string;
    phone?: string;
    company: string;
  };
  targetAudience?: string[];
  status?: string;
}

// Press Release API Functions

/**
 * Get all press releases for the authenticated user
 */
export const getPressReleases = async (): Promise<PressReleaseResponse[]> => {
  try {
    const response = await apiClient.get('/api/pr');
    return response.data.pressReleases || [];
  } catch (error: any) {
    console.error('Error fetching press releases:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch press releases');
  }
};

/**
 * Get a single press release by ID
 */
export const getPressRelease = async (id: string): Promise<PressReleaseResponse> => {
  try {
    const response = await apiClient.get(`/api/pr/${id}`);
    return response.data.pressRelease;
  } catch (error: any) {
    console.error('Error fetching press release:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch press release');
  }
};

/**
 * Get PR analytics with media outlet breakdown (publications)
 */
export const getPRAnalyticsWithOutlets = async (
  id: string
): Promise<PRAnalyticsWithOutletsResponse> => {
  try {
    const response = await apiClient.get(`/api/pr/${id}/analytics/outlets`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching PR analytics with outlets:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch PR analytics');
  }
};

/**
 * Create a new press release
 */
export const createPressRelease = async (
  data: CreatePressReleaseRequest
): Promise<PressReleaseResponse> => {
  try {
    const response = await apiClient.post('/api/pr', data);
    return response.data.pressRelease;
  } catch (error: any) {
    console.error('Error creating press release:', error);
    throw new Error(error.response?.data?.message || 'Failed to create press release');
  }
};

/**
 * Update a press release
 */
export const updatePressRelease = async (
  id: string,
  data: UpdatePressReleaseRequest
): Promise<PressReleaseResponse> => {
  try {
    const response = await apiClient.put(`/api/pr/${id}`, data);
    return response.data.pressRelease;
  } catch (error: any) {
    console.error('Error updating press release:', error);
    throw new Error(error.response?.data?.message || 'Failed to update press release');
  }
};

/**
 * Delete a press release
 */
export const deletePressRelease = async (id: string): Promise<void> => {
  try {
    await apiClient.delete(`/api/pr/${id}`);
  } catch (error: any) {
    console.error('Error deleting press release:', error);
    throw new Error(error.response?.data?.message || 'Failed to delete press release');
  }
};

/**
 * Submit a press release for distribution
 */
export const submitPressRelease = async (prId: string, data: any): Promise<any> => {
  try {
    const response = await apiClient.post('/api/pr/submit', {
      prId,
      ...data
    });
    return response.data;
  } catch (error: any) {
    console.error('Error submitting press release:', error);
    throw new Error(error.response?.data?.message || 'Failed to submit press release');
  }
};

/**
 * Get verification status, documents and AI analysis for a press release
 */
export const getVerificationStatus = async (
  id: string
): Promise<VerificationStatusApiResponse> => {
  try {
    const response = await apiClient.get(`/api/pr/${id}/verification/status`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching verification status:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch verification status');
  }
};

export default {
  getPressReleases,
  getPressRelease,
  createPressRelease,
  updatePressRelease,
  deletePressRelease,
  submitPressRelease,
  getPRAnalyticsWithOutlets,
  getVerificationStatus
};



